import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GameController, MagnifyingGlass, Plus, Trash, Storefront, Tag,
  DiceFive, Eye, Lightning, Trophy, CalendarDots, Star, X, Package
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  sold_here: { label: 'Sold Here', color: '#10B981', icon: Tag },
  demo_copy: { label: 'Demo Copy', color: '#F59E0B', icon: DiceFive },
  library_copy: { label: 'Library', color: '#3B82F6', icon: Package },
  available_for_free_play: { label: 'Free Play', color: '#06B6D4', icon: GameController },
  event_supported: { label: 'Events', color: '#9D4CDD', icon: CalendarDots },
  tournament_supported: { label: 'Tournaments', color: '#EF4444', icon: Trophy },
  currently_featured: { label: 'Featured', color: '#EC4899', icon: Star },
  temporarily_unavailable: { label: 'Unavailable', color: '#6B7280', icon: Eye },
};

export function ShopGameLibrary() {
  const { shopId } = useParams();
  const [inventory, setInventory] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [gameSearch, setGameSearch] = useState('');
  const [addStatus, setAddStatus] = useState('sold_here');
  const [addQty, setAddQty] = useState(1);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/shops/${shopId}/inventory/manage`, { withCredentials: true });
      setInventory(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const searchGames = useCallback(async (q) => {
    if (!q || q.length < 2) { setGames([]); return; }
    try {
      const res = await axios.get(`${API}/api/games-browse?search=${encodeURIComponent(q)}&limit=10`);
      setGames(res.data);
    } catch (e) { setGames([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchGames(gameSearch), 300);
    return () => clearTimeout(t);
  }, [gameSearch, searchGames]);

  const addInventory = async (gameId) => {
    try {
      await axios.post(`${API}/api/shops/${shopId}/inventory`, {
        game_id: gameId, inventory_status: addStatus, quantity: addQty,
      }, { withCredentials: true });
      toast.success('Added to inventory!');
      setShowAdd(false);
      setGameSearch('');
      setGames([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const updateInventory = async (id, data) => {
    try {
      await axios.put(`${API}/api/shops/${shopId}/inventory/${id}`, data, { withCredentials: true });
      toast.success('Updated!');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const removeInventory = async (id) => {
    try {
      await axios.delete(`${API}/api/shops/${shopId}/inventory/${id}`, { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (e) { toast.error('Remove failed'); }
  };

  const filtered = filter === 'all' ? inventory : inventory.filter(i => i.inventory_status === filter);
  const statusCounts = {};
  inventory.forEach(i => { statusCounts[i.inventory_status] = (statusCounts[i.inventory_status] || 0) + 1; });

  return (
    <div data-testid="shop-game-library">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Game Library</h2>
          <p className="text-sm text-[#666] mt-1">{inventory.length} entries across {Object.keys(statusCounts).length} categories</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="bg-[#10B981] hover:bg-[#10B981]/80 text-black font-semibold rounded-full px-4 py-2 text-sm flex items-center gap-1.5"
          data-testid="add-inventory-btn"
        >
          <Plus size={16} weight="bold" /> Add Game
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 overflow-x-auto pb-3 mb-4 hide-scrollbar">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
            filter === 'all' ? 'bg-[#10B981] text-black' : 'bg-white/[0.04] text-[#888]'
          }`}
        >
          All ({inventory.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
          statusCounts[k] ? (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                filter === k ? 'text-black' : 'text-[#888]'
              }`}
              style={filter === k ? { background: v.color } : { background: 'rgba(255,255,255,0.04)' }}
            >
              {v.label} ({statusCounts[k]})
            </button>
          ) : null
        ))}
      </div>

      {/* Inventory List */}
      {loading ? (
        <div className="text-center py-8 text-[#555]">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Storefront size={48} className="mx-auto text-[#333] mb-3" />
          <p className="text-[#666] text-sm">No inventory items. Add games to your library!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const cfg = STATUS_CONFIG[item.inventory_status] || STATUS_CONFIG.sold_here;
            const Icon = cfg.icon;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-3 flex items-center gap-3"
                data-testid={`inventory-item-${item.id}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cfg.color}15` }}>
                  <Icon size={20} weight="duotone" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{item.game_name || 'Unknown'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${cfg.color}20`, color: cfg.color }}>
                      {cfg.label}
                    </span>
                    <span className="text-[10px] text-[#555]">Qty: {item.quantity}</span>
                    {item.game_category && <span className="text-[10px] text-[#555]">· {item.game_category}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <select
                    value={item.inventory_status}
                    onChange={e => updateInventory(item.id, { inventory_status: e.target.value })}
                    className="bg-[#0a0a12] border border-white/[0.06] rounded text-[10px] text-[#888] p-1"
                    data-testid={`inv-status-${item.id}`}
                  >
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeInventory(item.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#666] hover:text-red-400"
                    data-testid={`inv-remove-${item.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Add Inventory Modal */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
            onClick={e => e.target === e.currentTarget && setShowAdd(false)}
          >
            <motion.div
              initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-[#0e0e16] border border-white/[0.06] rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
              data-testid="add-inventory-modal"
            >
              <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Add to Inventory</h3>
                <button onClick={() => setShowAdd(false)} className="text-[#666] hover:text-white"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-3">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <input
                    value={gameSearch} onChange={e => setGameSearch(e.target.value)}
                    placeholder="Search games..."
                    autoFocus
                    className="w-full bg-[#0a0a12] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#10B981]/40"
                    data-testid="inv-game-search"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-[#555] uppercase tracking-wider mb-1 block">Type</label>
                    <select value={addStatus} onChange={e => setAddStatus(e.target.value)}
                      className="w-full bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-2"
                      data-testid="inv-add-status"
                    >
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#555] uppercase tracking-wider mb-1 block">Quantity</label>
                    <input type="number" min="1" value={addQty} onChange={e => setAddQty(parseInt(e.target.value) || 1)}
                      className="w-full bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-2"
                      data-testid="inv-add-qty"
                    />
                  </div>
                </div>
                <div className="max-h-[40vh] overflow-y-auto space-y-1">
                  {games.map(g => (
                    <button key={g.id} onClick={() => addInventory(g.id)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] text-left"
                      data-testid={`inv-add-game-${g.slug}`}
                    >
                      <div className="w-8 h-8 rounded bg-[#10B981]/10 flex items-center justify-center shrink-0">
                        <GameController size={16} className="text-[#10B981]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{g.name}</p>
                        <p className="text-[#555] text-[10px]">{g.publisher} · {g.min_players}-{g.max_players}p</p>
                      </div>
                    </button>
                  ))}
                  {gameSearch.length >= 2 && games.length === 0 && (
                    <p className="text-center text-[#555] text-xs py-4">No games found</p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Available Tonight Controls for Shop Portal */
export function ShopAvailableTonight() {
  const { shopId } = useParams();
  const [availability, setAvailability] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [gameSearch, setGameSearch] = useState('');
  const [addState, setAddState] = useState('available_tonight');

  const AVAIL_STATES = {
    available_now: { label: 'Available Now', color: '#10B981' },
    available_tonight: { label: 'Tonight', color: '#F59E0B' },
    featured: { label: 'Featured', color: '#EC4899' },
    beginner_table_ready: { label: 'Beginner Ready', color: '#3B82F6' },
    reserved: { label: 'Reserved', color: '#9D4CDD' },
    in_use: { label: 'In Use', color: '#F97316' },
    unavailable: { label: 'Unavailable', color: '#6B7280' },
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/availability/games?target_type=shop&target_id=${shopId}`, { withCredentials: true });
      setAvailability(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const searchGames = useCallback(async (q) => {
    if (!q || q.length < 2) { setGames([]); return; }
    try {
      const res = await axios.get(`${API}/api/games-browse?search=${encodeURIComponent(q)}&limit=10`);
      setGames(res.data);
    } catch (e) { setGames([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchGames(gameSearch), 300);
    return () => clearTimeout(t);
  }, [gameSearch, searchGames]);

  const addAvailability = async (gameId) => {
    try {
      await axios.post(`${API}/api/availability/games`, {
        availability_target_type: 'shop', availability_target_id: shopId,
        game_id: gameId, availability_state: addState,
      }, { withCredentials: true });
      toast.success('Availability set!');
      setShowAdd(false);
      setGameSearch('');
      setGames([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateAvailability = async (id, state) => {
    try {
      await axios.put(`${API}/api/availability/games/${id}`, { availability_state: state }, { withCredentials: true });
      toast.success('Updated!');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const removeAvailability = async (id) => {
    try {
      await axios.delete(`${API}/api/availability/games/${id}`, { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (e) { toast.error('Remove failed'); }
  };

  return (
    <div data-testid="shop-available-tonight">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Available Tonight</h3>
          <p className="text-xs text-[#666]">Show players what's ready to play</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-semibold rounded-full px-3 py-1.5 text-xs flex items-center gap-1"
          data-testid="add-availability-btn"
        >
          <Plus size={14} weight="bold" /> Set Available
        </button>
      </div>

      {loading ? <div className="text-[#555] text-sm">Loading...</div> : (
        <div className="space-y-2">
          {availability.length === 0 ? (
            <div className="text-center py-8">
              <Lightning size={36} className="mx-auto text-[#333] mb-2" />
              <p className="text-[#555] text-xs">No games set as available. Let players know what they can play!</p>
            </div>
          ) : (
            availability.map(a => {
              const stCfg = AVAIL_STATES[a.availability_state] || AVAIL_STATES.available_now;
              return (
                <div key={a.id} className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-3 flex items-center gap-3"
                  data-testid={`avail-item-${a.id}`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stCfg.color}15` }}>
                    <Lightning size={16} weight="duotone" style={{ color: stCfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{a.game_name || 'Unknown'}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${stCfg.color}20`, color: stCfg.color }}>
                      {stCfg.label}
                    </span>
                  </div>
                  <select value={a.availability_state} onChange={e => updateAvailability(a.id, e.target.value)}
                    className="bg-[#0a0a12] border border-white/[0.06] rounded text-[10px] text-[#888] p-1"
                  >
                    {Object.entries(AVAIL_STATES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <button onClick={() => removeAvailability(a.id)} className="p-1 hover:bg-red-500/10 rounded text-[#666] hover:text-red-400">
                    <Trash size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add Availability Modal */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
            onClick={e => e.target === e.currentTarget && setShowAdd(false)}
          >
            <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-[#0e0e16] border border-white/[0.06] rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
              data-testid="add-availability-modal"
            >
              <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Set Available Game</h3>
                <button onClick={() => setShowAdd(false)} className="text-[#666] hover:text-white"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-3">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <input value={gameSearch} onChange={e => setGameSearch(e.target.value)}
                    placeholder="Search games..." autoFocus
                    className="w-full bg-[#0a0a12] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#F59E0B]/40"
                    data-testid="avail-game-search"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#555] uppercase tracking-wider mb-1 block">Status</label>
                  <select value={addState} onChange={e => setAddState(e.target.value)}
                    className="w-full bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-2"
                  >
                    {Object.entries(AVAIL_STATES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div className="max-h-[40vh] overflow-y-auto space-y-1">
                  {games.map(g => (
                    <button key={g.id} onClick={() => addAvailability(g.id)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] text-left"
                    >
                      <div className="w-8 h-8 rounded bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                        <GameController size={16} className="text-[#F59E0B]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{g.name}</p>
                        <p className="text-[#555] text-[10px]">{g.publisher}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
